"use client";

import React from "react";
import WiderTalkHeading from "../components/ui/WiderTalkHeading";

export default function TermsAndConditionsPage() {
  return (
    <div className="min-h-[calc(100vh-60px)] bg-widertalk-cream/50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <WiderTalkHeading>Terms and Conditions</WiderTalkHeading>

        <div className="bg-white rounded-lg shadow-md p-8 space-y-8">
          <div className="text-sm text-gray-600 mb-6">
            <p><strong>Last Updated:</strong> October 15, 2025</p>
            <p><strong>Effective Date:</strong> October 15, 2025</p>
          </div>

          <section>
            <h2 className="text-2xl font-semibold text-widertalk-navy mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              By accessing and using WiderTalk (the "Platform"), operated by WIDERTALK LTD ("we," "us," or "our"),
              you agree to be bound by these Terms and Conditions ("Terms"). If you do not agree to these Terms,
              please do not use our Platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-widertalk-navy mb-4">2. Content Standards and Prohibited Content</h2>
            <div className="space-y-4">
              <p className="text-gray-700 leading-relaxed">
                <strong>2.1 No Explicit Content:</strong> WiderTalk maintains a strict zero-tolerance policy regarding
                explicit content. Users are prohibited from uploading, streaming, sharing, or distributing any content
                that contains:
              </p>
              <ul className="list-disc list-inside text-gray-700 ml-4 space-y-2">
                <li>Nudity or sexually explicit material</li>
                <li>Pornographic content of any kind</li>
                <li>Sexual acts or suggestive content</li>
                <li>Graphic violence or gore</li>
                <li>Content promoting hate speech, discrimination, or harassment</li>
                <li>Illegal activities or content that violates applicable laws</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                <strong>2.2 Platform Moderation:</strong> WiderTalk reserves the absolute right to remove any user
                from the Platform immediately and without prior notice if they violate these content standards.
                This includes but is not limited to permanent account suspension and blocking access to all Platform services.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-widertalk-navy mb-4">3. Sports Content and Licensing Compliance</h2>
            <div className="space-y-4">
              <p className="text-gray-700 leading-relaxed">
                <strong>3.1 Licensed Content:</strong> WiderTalk operates under specific media rights agreements
                for sports content, including professional cycling events. All content broadcast on our Platform
                must comply with the terms of our licensing agreements.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>3.2 Geographic Restrictions:</strong> Certain content may only be available in specific
                territories as defined by our licensing agreements. Users accessing content outside authorized
                territories may face account restrictions.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>3.3 Redistribution Prohibited:</strong> Users are strictly prohibited from:
              </p>
              <ul className="list-disc list-inside text-gray-700 ml-4 space-y-2">
                <li>Recording, downloading, or capturing any live or on-demand content</li>
                <li>Redistributing WiderTalk content on any third-party platform including YouTube, Facebook, X (Twitter), or any social media platform</li>
                <li>Creating derivative works from our licensed content</li>
                <li>Using our content for commercial purposes without explicit written permission</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-widertalk-navy mb-4">4. User Accounts and Commentary</h2>
            <div className="space-y-4">
              <p className="text-gray-700 leading-relaxed">
                <strong>4.1 Account Responsibility:</strong> Users are responsible for maintaining the confidentiality
                of their account credentials and for all activities that occur under their account.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>4.2 User Conduct Standards:</strong> When providing live commentary or participating in chat,
                users must maintain professional standards appropriate for sports broadcasting. This includes:
              </p>
              <ul className="list-disc list-inside text-gray-700 ml-4 space-y-2">
                <li>Respectful language and behavior towards all users and athletes</li>
                <li>Accurate information when possible - avoid spreading misinformation</li>
                <li>No harassment, bullying, or intimidation of other users or athletes</li>
                <li>No use of profanity, offensive language, or discriminatory remarks</li>
                <li>Professional conduct suitable for a general sports audience</li>
                <li>No spam, excessive self-promotion, or irrelevant content</li>
                <li>Respect for the integrity of the sport and competition</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                <strong>4.3 User Moderation:</strong> WiderTalk reserves the absolute right to remove any user
                from the Platform immediately and without prior notice if they violate these conduct standards.
                This includes but is not limited to permanent account suspension and blocking access to all Platform services.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-widertalk-navy mb-4">5. Commentator Views and Editorial Independence</h2>
            <div className="space-y-4">
              <p className="text-gray-700 leading-relaxed">
                <strong>5.1 Independence of Commentator Views:</strong> WIDERTALK LTD does not hold, endorse, or take responsibility for any views, opinions, statements, analyses, predictions, or commentary expressed by individual commentators on our Platform. All commentary represents the personal views and opinions of the individual commentators and does not reflect the views, positions, or endorsements of WIDERTALK LTD.
              </p>
              
              <p className="text-gray-700 leading-relaxed">
                <strong>5.2 Editorial Independence:</strong> Commentators maintain full editorial independence over their commentary content, subject to compliance with our content standards and broadcasting regulations. WiderTalk does not direct, influence, or control the editorial content, opinions, or analytical perspectives provided by commentators during live broadcasts.
              </p>
              
              <p className="text-gray-700 leading-relaxed">
                <strong>5.3 Platform as Neutral Facilitator:</strong> WiderTalk operates as a neutral technology platform that facilitates the distribution of sports commentary. We do not:
              </p>
              <ul className="list-disc list-inside text-gray-700 ml-4 space-y-2">
                <li>Endorse or validate any sports analysis, predictions, or opinions expressed by commentators</li>
                <li>Take responsibility for the accuracy of commentary, statistics, or information provided by individual commentators</li>
                <li>Support or oppose any athletes, teams, sponsors, or sporting organizations mentioned in commentary</li>
                <li>Guarantee the professional qualifications or expertise of any commentator using our Platform</li>
                <li>Verify or fact-check real-time commentary content during live broadcasts</li>
                <li>Accept liability for decisions made by users based on commentator opinions or analysis</li>
              </ul>
              
              <p className="text-gray-700 leading-relaxed">
                <strong>5.4 User Acknowledgment and Disclaimer:</strong> By using WiderTalk, all users acknowledge and agree that:
              </p>
              <ul className="list-disc list-inside text-gray-700 ml-4 space-y-2">
                <li>Commentary represents individual opinions and should not be considered professional advice</li>
                <li>Users must exercise their own judgment when considering commentator analysis or predictions</li>
                <li>WiderTalk is not responsible for any financial, betting, or other decisions made based on commentator content</li>
                <li>Commentator views on athletes, teams, tactics, or race outcomes are personal opinions, not official positions</li>
                <li>Users should seek additional sources and professional advice for any investment or betting decisions</li>
              </ul>
              
              <p className="text-gray-700 leading-relaxed">
                <strong>5.5 Liability Protection:</strong> WIDERTALK LTD shall not be held liable for any damages, losses, or consequences arising from:
              </p>
              <ul className="list-disc list-inside text-gray-700 ml-4 space-y-2">
                <li>Disagreement with or reliance upon commentator opinions, analysis, or predictions</li>
                <li>Inaccurate information provided by commentators during live broadcasts</li>
                <li>Controversial or unpopular opinions expressed by individual commentators</li>
                <li>Commentary that may be considered biased toward or against particular athletes, teams, or nations</li>
                <li>Technical analysis or race predictions that prove incorrect</li>
                <li>Personal or professional disputes arising from commentator content</li>
                <li>Financial losses resulting from decisions made based on commentator analysis</li>
              </ul>
              
              <p className="text-gray-700 leading-relaxed">
                <strong>5.6 Reporting and Moderation:</strong> While WiderTalk maintains editorial independence, users may report commentary that violates our content standards. We reserve the right to investigate and take appropriate action for content that breaches our Terms and Conditions, while respecting commentator editorial freedom within our established guidelines.
              </p>
              
              <p className="text-gray-700 leading-relaxed">
                <strong>5.7 International Broadcasting Compliance:</strong> This editorial independence operates within the framework of international broadcasting standards and regulations. Commentators remain responsible for ensuring their content complies with applicable broadcasting laws and regulations in territories where their commentary is accessible.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-widertalk-navy mb-4">6. Commentator Sponsorship and Advertising</h2>
            <div className="space-y-4">
              <p className="text-gray-700 leading-relaxed">
                <strong>6.1 Permitted Commentator Sponsorship:</strong> Commentators providing audio-only commentary
                are permitted to include personal sponsorship messages and advertisements in their commentary streams,
                subject to the following mandatory requirements:
              </p>

              <p className="text-gray-700 leading-relaxed">
                <strong>6.2 Prior Approval Required:</strong> All commentator sponsorship and advertising content must receive
                explicit written approval from WIDERTALK LTD before being included in any commentary stream. Commentators
                must submit sponsorship content for review at least 48 hours before the intended broadcast.
              </p>

              <p className="text-gray-700 leading-relaxed">
                <strong>6.3 Mandatory Disclaimers:</strong> All commentator sponsorship content must include clear,
                explicit disclaimers stating:
              </p>
              <ul className="list-disc list-inside text-gray-700 ml-4 space-y-2">
                <li>That the sponsorship message is from the individual commentator, not from WiderTalk</li>
                <li>That the sponsor is not affiliated with or endorsed by WiderTalk</li>
                <li>That the sponsor is not associated with the event, organizers, or official event sponsors</li>
                <li>Clear verbal distinction between personal sponsorship content and official commentary</li>
                <li>Use of internationally recognized disclosure language such as "This is a paid advertisement" or "Sponsored content" at the beginning of sponsored segments</li>
                <li>Compliance with digital platform disclosure requirements including hashtag disclosures (#ad, #sponsored, #partnership) where applicable</li>
                <li>In territories requiring bilingual disclosures, appropriate local language disclaimers must be provided</li>
                <li>Disclosure timing must meet local jurisdiction requirements (e.g., within first 15 seconds of sponsored content)</li>
              </ul>

              <p className="text-gray-700 leading-relaxed">
                <strong>6.4 Prohibited Sponsorship Content:</strong> Commentators are strictly prohibited from including
                sponsorship or advertising that:
              </p>
              <ul className="list-disc list-inside text-gray-700 ml-4 space-y-2">
                <li>Has not received prior written approval from WIDERTALK LTD</li>
                <li>Suggests or implies any association with WiderTalk, the event, or official event sponsors</li>
                <li>Conflicts with or competes with official WiderTalk sponsors or event sponsors</li>
                <li>Violates any applicable advertising standards or broadcasting regulations in any territory where content may be accessed</li>
                <li>Promotes illegal activities, explicit content, harmful products, or restricted substances in any jurisdiction worldwide</li>
                <li>Fails to meet local consumer protection laws regarding advertising disclosure and transparency</li>
                <li>Interferes with the quality or flow of sports commentary</li>
                <li>Contains misleading or deceptive claims about products or services</li>
              </ul>

              <p className="text-gray-700 leading-relaxed">
                <strong>6.5 International Broadcasting Standards Compliance:</strong> All commentator sponsorship content must
                comply with international broadcasting and advertising standards, including but not limited to:
              </p>
              <ul className="list-disc list-inside text-gray-700 ml-4 space-y-2">
                <li><strong>United Kingdom:</strong> ASA (Advertising Standards Authority) guidelines for advertising disclosure and transparency</li>
                <li><strong>United States:</strong> FTC (Federal Trade Commission) requirements for sponsored content disclosure and endorsement guidelines</li>
                <li><strong>Canada:</strong> CRTC (Canadian Radio-television and Telecommunications Commission) standards and Competition Bureau guidelines</li>
                <li><strong>Australia:</strong> ACMA (Australian Communications and Media Authority) commercial disclosure rules and ACCC consumer protection standards</li>
                <li><strong>European Union:</strong> Audiovisual Media Services Directive (AVMSD) and consumer protection regulations</li>
                <li><strong>New Zealand:</strong> Broadcasting Standards Authority guidelines and Commerce Commission advertising standards</li>
                <li><strong>South Africa:</strong> Independent Communications Authority (ICASA) broadcasting regulations</li>
                <li><strong>India:</strong> Advertising Standards Council guidelines and Broadcasting Content Complaints Council standards</li>
                <li><strong>Brazil:</strong> National Council of Self-Regulation Advertising (CONAR) standards</li>
                <li><strong>Japan:</strong> Japan Advertising Review Organization (JARO) guidelines</li>
                <li>Local advertising standards and consumer protection laws in all other territories where content is accessible</li>
              </ul>

              <p className="text-gray-700 leading-relaxed">
                <strong>6.6 Violation Consequences:</strong> Unauthorized sponsorship content or failure to comply with
                these requirements will result in immediate removal of commentary privileges and may lead to permanent
                account suspension. WiderTalk reserves the right to terminate commentator access without notice for
                sponsorship violations.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-widertalk-navy mb-4">7. Intellectual Property Rights</h2>
            <p className="text-gray-700 leading-relaxed">
              All content on WiderTalk, including but not limited to video streams, graphics, logos, and user interfaces,
              is protected by copyright and other intellectual property laws. Our sports content is licensed from third-party
              rights holders, and unauthorized use may result in legal action.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-widertalk-navy mb-4">8. Privacy and Data Protection</h2>
            <p className="text-gray-700 leading-relaxed">
              Your privacy is important to us. Please review our Privacy Policy, which also governs your use of the Platform,
              to understand our practices regarding the collection and use of your personal information.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-widertalk-navy mb-4">9. Platform Availability and Modifications</h2>
            <div className="space-y-4">
              <p className="text-gray-700 leading-relaxed">
                <strong>9.1 Service Availability:</strong> While we strive to maintain continuous service,
                WiderTalk does not guarantee uninterrupted access to the Platform. We may suspend or discontinue
                the service for maintenance, updates, or other operational reasons.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>9.2 Platform Modifications:</strong> We reserve the right to modify, update, or discontinue
                any aspect of the Platform at any time without prior notice.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-widertalk-navy mb-4">10. Limitation of Liability</h2>
            <p className="text-gray-700 leading-relaxed">
              To the fullest extent permitted by law, WIDERTALK LTD shall not be liable for any indirect, incidental,
              special, consequential, or punitive damages, including but not limited to loss of profits, data, or use,
              arising out of or relating to your use of the Platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-widertalk-navy mb-4">11. Termination</h2>
            <div className="space-y-4">
              <p className="text-gray-700 leading-relaxed">
                <strong>11.1 Immediate Termination:</strong> WiderTalk reserves the right to terminate or suspend
                your account immediately, without prior notice or liability, for any reason, including but not limited to:
              </p>
              <ul className="list-disc list-inside text-gray-700 ml-4 space-y-2">
                <li>Violation of these Terms and Conditions</li>
                <li>Posting or sharing explicit or inappropriate content</li>
                <li>Engaging in behavior that damages the Platform's reputation</li>
                <li>Violating applicable laws or regulations</li>
                <li>Attempting to redistribute or illegally share licensed content</li>
                <li>Unauthorized sponsorship or advertising content</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                <strong>11.2 Effect of Termination:</strong> Upon termination, your right to use the Platform will
                cease immediately, and you must discontinue all use of our services.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-widertalk-navy mb-4">12. Governing Law and Jurisdiction</h2>
            <p className="text-gray-700 leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of England and Wales.
              Any disputes arising out of or relating to these Terms shall be subject to the exclusive jurisdiction
              of the courts of England and Wales.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-widertalk-navy mb-4">13. Broadcasting Standards and Regulatory Compliance</h2>
            <div className="space-y-4">
              <p className="text-gray-700 leading-relaxed">
                <strong>13.1 International Broadcasting Regulations:</strong> As a global sports streaming platform operating worldwide (excluding China and Macao), WiderTalk operates under strict broadcasting compliance requirements across all territories where content is accessible, including:
              </p>
              <ul className="list-disc list-inside text-gray-700 ml-4 space-y-2">
                <li><strong>United Kingdom:</strong> Ofcom Broadcasting Code standards for decency, fairness, and accuracy; ASA advertising guidelines; watershed rules (family-friendly content before 9 PM)</li>
                <li><strong>United States:</strong> FCC broadcasting regulations; ADA accessibility compliance requirements; Section 508 accessibility standards</li>
                <li><strong>Canada:</strong> CRTC broadcasting standards; bilingual content requirements where applicable; Canadian accessibility standards (AODA)</li>
                <li><strong>Australia:</strong> ACMA broadcasting codes; Australian Disability Discrimination Act accessibility requirements; consumer protection regulations</li>
                <li><strong>European Union:</strong> Audiovisual Media Services Directive (AVMSD); European Accessibility Act requirements; digital rights compliance</li>
                <li><strong>India:</strong> Broadcasting Content Complaints Council standards; accessibility guidelines for persons with disabilities</li>
                <li><strong>Brazil:</strong> ANATEL broadcasting regulations; accessibility requirements under Brazilian legislation</li>
                <li><strong>Japan:</strong> Broadcasting standards and accessibility regulations for persons with disabilities</li>
                <li><strong>New Zealand:</strong> Broadcasting Standards Authority codes and accessibility requirements</li>
                <li><strong>South Africa:</strong> ICASA broadcasting regulations and accessibility standards</li>
                <li>Local broadcasting standards, content regulations, and accessibility requirements in all other territories where content is accessible</li>
              </ul>

              <p className="text-gray-700 leading-relaxed">
                <strong>13.2 Accessibility and Inclusive Design Standards:</strong> All Platform broadcasts must maintain compliance with international accessibility requirements:
              </p>
              <ul className="list-disc list-inside text-gray-700 ml-4 space-y-2">
                <li><strong>Audio Accessibility:</strong> Professional audio quality with clear sound transmission and audio description capabilities where required by law</li>
                <li><strong>Visual Accessibility:</strong> Closed captioning and subtitles in compliance with local accessibility laws (WCAG 2.1 AA standards)</li>
                <li><strong>Interactive Accessibility:</strong> Keyboard navigation support and screen reader compatibility for users with disabilities</li>
                <li><strong>Language Accessibility:</strong> Multiple language support where required by local broadcasting standards</li>
                <li><strong>Technical Accessibility:</strong> Appropriate contrast ratios, font sizes, and user interface elements for accessibility compliance</li>
                <li><strong>Streaming Accessibility:</strong> Compatible with assistive technologies and accessibility software</li>
                <li>Reliable streaming infrastructure with minimal downtime to ensure consistent access globally</li>
              </ul>

              <p className="text-gray-700 leading-relaxed">
                <strong>13.3 Sports Broadcasting and Content Standards:</strong> Our sports content must adhere to:
              </p>
              <ul className="list-disc list-inside text-gray-700 ml-4 space-y-2">
                <li><strong>UCI Regulations:</strong> Compliance with Union Cycliste Internationale standards for cycling broadcasts worldwide</li>
                <li><strong>International Sporting Standards:</strong> Adherence to anti-doping messaging and fair play principles globally</li>
                <li><strong>Copyright Protection:</strong> Strict compliance with international copyright laws and licensing agreements across all territories</li>
                <li><strong>Geographic Licensing:</strong> Respect for territorial broadcasting rights as defined by our media licensing agreements</li>
                <li><strong>Athlete Privacy:</strong> Compliance with international privacy laws regarding athlete data, imagery, and personal information</li>
                <li><strong>Sports Integrity:</strong> Support for anti-corruption and match-fixing prevention initiatives worldwide</li>
                <li><strong>Cultural Sensitivity:</strong> Appropriate content for diverse international audiences and cultural contexts</li>
              </ul>

              <p className="text-gray-700 leading-relaxed">
                <strong>13.4 Data Protection and Privacy Compliance:</strong> WiderTalk adheres to comprehensive international data protection standards:
              </p>
              <ul className="list-disc list-inside text-gray-700 ml-4 space-y-2">
                <li><strong>United Kingdom:</strong> UK GDPR and Data Protection Act 2018</li>
                <li><strong>European Union:</strong> General Data Protection Regulation (GDPR) across all EU member states</li>
                <li><strong>United States:</strong> California Consumer Privacy Act (CCPA), California Privacy Rights Act (CPRA), and state-specific privacy laws</li>
                <li><strong>Canada:</strong> Personal Information Protection and Electronic Documents Act (PIPEDA) and provincial privacy legislation</li>
                <li><strong>Australia:</strong> Privacy Act 1988 and Australian Privacy Principles</li>
                <li><strong>Brazil:</strong> Lei Geral de Proteção de Dados (LGPD)</li>
                <li><strong>Asia-Pacific:</strong> Personal Data Protection Acts in Singapore, Thailand, and other applicable territories</li>
                <li><strong>Other Territories:</strong> Local data protection and privacy laws in all jurisdictions where we operate or users access our content</li>
              </ul>

              <p className="text-gray-700 leading-relaxed">
                <strong>13.5 Content Accuracy and Editorial Standards:</strong> WiderTalk ensures worldwide compliance with:
              </p>
              <ul className="list-disc list-inside text-gray-700 ml-4 space-y-2">
                <li>Factual accuracy in sports information, statistics, and race data across all territories and languages</li>
                <li>Impartial and balanced coverage without bias against athletes, teams, or nations</li>
                <li>Respect for athlete privacy and dignity in all broadcasts worldwide, in compliance with local privacy laws</li>
                <li>Appropriate acknowledgment of sponsors and licensing partners in compliance with local disclosure laws</li>
                <li>Compliance with sports integrity principles and anti-corruption measures globally</li>
                <li>Cultural sensitivity and appropriate content standards for diverse international audiences</li>
                <li>Language accessibility and localization where required by local broadcasting standards</li>
                <li>Time zone considerations and appropriate scheduling for global audiences</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-widertalk-navy mb-4">14. Contact Information</h2>
            <div className="bg-gray-50 rounded-lg p-6">
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have any questions about these Terms and Conditions, please contact us at:
              </p>
              <div className="text-gray-700">
                <p><strong>WIDERTALK LTD</strong></p>
                <p>Happiness at Work</p>
                <p>1 Green Bank, London E1W 2PA</p>
                <p>United Kingdom</p>
                <p>VAT: GB 478569619</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-widertalk-navy mb-4">15. Updates to Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update these Terms and Conditions from time to time. When we do, we will post the updated
              Terms on this page and update the "Last Updated" date. Your continued use of the Platform after
              any changes constitutes acceptance of the new Terms.
            </p>
          </section>

          <div className="border-t border-gray-200 pt-6 mt-8">
            <p className="text-sm text-gray-600 italic">
              By using WiderTalk, you acknowledge that you have read, understood, and agree to be bound by these
              Terms and Conditions. WiderTalk reserves the right to remove any user who violates these terms,
              particularly regarding explicit content, which is strictly prohibited on our platform.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
